import React, { useContext } from 'react';
import classNames from 'classnames';
import { Box, BoxProps, TextButton } from '@wix/design-system';
import { ArrowLeft } from '@wix/wix-ui-icons-common';
import { MainHeader, MainHeaderProps } from '../MainHeader';
import s from './DialogContainer.scss';
import { observer } from 'mobx-react';
import { AppContextProvider } from '../AppLoader';
import { ModalHeader } from '../ModalHeader';
import { useTranslation } from 'react-i18next';
import { dataHooks } from '../../dataHooks';

export interface DialogContainerProps extends BoxProps {
  mainHeaderProps?: MainHeaderProps; // default is empty object
  showMainHeader?: boolean; // default is true
  dataHook?: string;
  contentClasses?: any[];
  closeButtonDataHook?: string;
}

export const DialogContainer: React.FC<DialogContainerProps> = observer(
  ({
    children,
    mainHeaderProps,
    showMainHeader = true,
    dataHook,
    className = [],
    contentClasses = [],
    closeButtonDataHook,
    ...props
  }) => {
    const {
      rootStore: { displayStore, onHeaderBackButtonClicked, navigationStore },
    } = useContext(AppContextProvider);
    const { t } = useTranslation();
    const isLargeLogo =
      parseInt(displayStore.preset.logo?.height ?? '0', 10) > 48;

    // Determine if back button should show above dialog
    const showBackAboveDialog =
      displayStore.isBackButtonAboveDialog && // feature enabled
      navigationStore.backButtonVisibility !== 'hidden' && // not explicitly hidden
      (navigationStore.backButtonVisibility === 'visible' || // explicitly visible
        (mainHeaderProps?.showBackButton !== false && // prop allows it
          !displayStore.isMobile)); // and not on mobile

    return (
      <>
        {showMainHeader && displayStore.showMainHeader && (
          <MainHeader {...mainHeaderProps} />
        )}
        <Box
          direction="vertical"
          align="center"
          {...props}
          className={classNames(
            displayStore.isMobile ? s.mobileContainer : s.desktopContainer,
            {
              [s.largeLogo]: isLargeLogo,
              [s.vertical]: displayStore.isVerticalLayout,
            },
            s[displayStore.dialogContainerClass],
            className,
          )}
          dataHook={dataHook}
        >
          {displayStore.showModalHeader && (
            <ModalHeader
              showBackButton={mainHeaderProps?.showBackButton}
              closeButtonDataHook={closeButtonDataHook}
            />
          )}
          {showBackAboveDialog && (
            <Box
              className={s.backButtonAboveDialog}
              width="100%"
              maxWidth="500px"
            >
              <TextButton
                dataHook={dataHooks.app.backBtn}
                prefixIcon={<ArrowLeft />}
                size="medium"
                weight="normal"
                skin="dark"
                underline="none"
                onClick={() =>
                  onHeaderBackButtonClicked(mainHeaderProps?.backButtonCallback)
                }
              >
                {t('dialogHeader.backButton.text')}
              </TextButton>
            </Box>
          )}
          <div className={classNames(...contentClasses, s.dialogEnter)}>
            {children}
          </div>
        </Box>
      </>
    );
  },
);

export const DialogFormContainer: React.FC<
  DialogContainerProps & { formClasses?: string[] }
> = ({ children, formClasses = [], ...props }) => {
  const {
    rootStore: {
      displayStore: { isVerticalLayout, isMobile, isMobileFullScreen },
    },
  } = useContext(AppContextProvider);
  return (
    <DialogContainer
      {...props}
      contentClasses={[
        classNames(
          s.form,
          {
            [s.vertical]: isVerticalLayout,
            [s.mobile]: isMobile,
            [s.mobileFullScreen]: isMobileFullScreen,
          },
          ...formClasses,
        ),
      ]}
    >
      {children}
    </DialogContainer>
  );
};
